import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useTabsStore } from "./tabsStore";

type TabPaneContextValue = {
  tabKey: string;
  active: boolean;
};

export const TabPaneContext = createContext<TabPaneContextValue>({
  tabKey: "",
  active: true,
});

/**
 * Lets a page set its own tab title (e.g. the request number once loaded).
 * No-op outside the tabbed workspace.
 */
export function useTabTitle(title?: string | null): void {
  const { tabKey } = useContext(TabPaneContext);
  const updateTitle = useTabsStore((s) => s.updateTitle);
  useEffect(() => {
    if (tabKey && title) updateTitle(tabKey, title);
  }, [tabKey, title, updateTitle]);
}

/**
 * True when this tab is the visible one. Pages can use it to pause polling or
 * expensive work while backgrounded.
 */
export function useIsTabActive(): boolean {
  return useContext(TabPaneContext).active;
}

/**
 * Re-runs `refresh` every time this tab goes from background to foreground.
 *
 * Tabs are keep-alive: a background pane stays mounted, so coming back to it is
 * a focus change, not a remount, and nothing re-fetches. Anything that changed
 * meanwhile — a request edited in another tab, an approval granted, a section
 * submitted — is invisible until a hard reload. Any page showing server data
 * that another page can change should call this.
 *
 * Deliberately skips the first activation: the page already loads on mount, and
 * firing here too would double every initial fetch.
 *
 * Not for pages holding unsaved user input — refetching under an open form can
 * throw away what the user typed.
 */
export function useRefreshOnTabFocus(refresh: () => void): void {
  const { active } = useContext(TabPaneContext);
  // Held in a ref so a caller passing an inline arrow doesn't re-arm the effect
  // on every render (which would refresh in a loop).
  const latest = useRef(refresh);
  const wasActive = useRef(active);

  useEffect(() => {
    latest.current = refresh;
  });

  useEffect(() => {
    if (active && !wasActive.current) latest.current();
    wasActive.current = active;
  }, [active]);
}

/**
 * Closes the tab this page is rendered in.
 *
 * Call it *before* navigating away: TabbedWorkspace opens a tab for whatever
 * pathname is current, so closing after the navigation would race with that
 * effect and leave the old tab behind.
 */
export function useCloseOwnTab(): () => void {
  const { tabKey } = useContext(TabPaneContext);
  const removeTab = useTabsStore((s) => s.removeTab);
  return useCallback(() => {
    if (tabKey) removeTab(tabKey);
  }, [tabKey, removeTab]);
}

export const TabPaneProvider: React.FC<
  React.PropsWithChildren<TabPaneContextValue>
> = ({ tabKey, active, children }) => (
  <TabPaneContext.Provider value={{ tabKey, active }}>
    {children}
  </TabPaneContext.Provider>
);
